import { ArrowsInSimpleIcon, ArrowsOutSimpleIcon } from '@phosphor-icons/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/business/market-shop-restaurant/button'
import { Icon } from '@/components/ui/icon'
import { RadialLoader } from '@/components/ui/loaders/radial-loader'
import { Typography } from '@/components/ui/typography'
import { MarketShopOrderStatus, marketShopOrderStatusDataMap } from '@/models'
import { getMarketShopRestaurantStatusLabelsMap } from '@/pages/market-shop-restaurant/market-shop-restaurant.constants'
import { useMarketShopRestaurantStore } from '@/pages/market-shop-restaurant/market-shop-restaurant.store'

import { OrderCard } from './order-card'
import * as S from './orders-column.styled'

export interface OrdersColumnProps {
  children: React.ReactNode
  status: MarketShopOrderStatus
  count: number
  loading: boolean
}

export const OrdersColumn = ({ children, status, count, loading }: OrdersColumnProps) => {
  const { t } = useTranslation(['market-shop-restaurant'])
  const { draggedOrder, dropTargetColumn } = useMarketShopRestaurantStore()
  const statusLabels = getMarketShopRestaurantStatusLabelsMap(t)

  const canExpandOrShrink = status === 'completed' || status === 'declined'

  const [isCollapsed, setIsCollapsed] = useState(canExpandOrShrink)

  const handleToggleColumn = () => {
    setIsCollapsed((prev) => !prev)
  }

  const isHighlighted =
    !!draggedOrder &&
    dropTargetColumn === status &&
    marketShopOrderStatusDataMap[draggedOrder.status].validTransitions.includes(status)

  const isDimmed =
    !!draggedOrder &&
    !marketShopOrderStatusDataMap[draggedOrder.status].validTransitions.includes(status)

  const showPreview = isHighlighted && draggedOrder.status !== status

  return (
    <S.OrdersColumn
      $status={status}
      $isCollapsed={isCollapsed}
      $isHighlighted={isHighlighted}
      $isDimmed={isDimmed}
      data-column-type={status}
    >
      <S.Header $status={status} $isCollapsed={isCollapsed}>
        <Icon icon={marketShopOrderStatusDataMap[status].icon} size="4rem" />
        <Typography as="span" size="text-large" weight="semibold" breakLines={true}>
          {statusLabels[status]}
        </Typography>
        {canExpandOrShrink && (
          <Button onlyBox use="unset" size="small" onClick={handleToggleColumn}>
            <Icon icon={isCollapsed ? ArrowsOutSimpleIcon : ArrowsInSimpleIcon} size="2rem" />
          </Button>
        )}
        <S.Count $status={status}>
          {loading ? (
            <RadialLoader />
          ) : (
            <Typography as="span" size="heading-medium" weight="medium">
              {count}
            </Typography>
          )}
        </S.Count>
      </S.Header>
      <S.Content>
        {showPreview && (
          <OrderCard order={draggedOrder} isPreview={true} previewColumnStatus={status} />
        )}
        {children}
      </S.Content>
    </S.OrdersColumn>
  )
}
